import { StyleSheet, Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

import { Block } from "src/core/components/pdf/shared/Block";
import { RiskDot } from "src/core/components/pdf/shared/RiskDot";
import { Title } from "src/core/components/pdf/shared/Title";
import {
  FolderFileType,
  LabQuestion,
  SchemaProperties,
} from "src/core/components/pdf/shared/types";
import { CustomRiskLevel } from "src/core/constants/risk";

const styles = StyleSheet.create({
  labQuestionsBlock: {
    backgroundColor: "#FDFDFE",
    borderWidth: 1,
    // équivalent à #0A1A531A
    borderColor: "#E6E8ED",
    borderRadius: 12,
    padding: 12,
    gap: 12,
  },
  labQuestionRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  labQuestionLeft: {
    width: "80%",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  labQuestionRight: {
    width: "20%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
  },
  labQuestionText: {
    fontSize: 10,
    color: "#0A1A53",
    flex: 1,
    lineHeight: 1.4,
  },
  labRadioButtonContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  labRadioButton: {
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#6690A3",
    alignItems: "center",
    justifyContent: "center",
  },
  labRadioButtonChecked: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: "#6690A3",
  },
  labRadioButtonText: {
    fontSize: 9,
    color: "#0A1A53",
    marginLeft: 4,
  },
});

type LabSectionProps = {
  folderFile: FolderFileType;
};

export const LabSection = ({ folderFile }: LabSectionProps) => {
  const { t } = useTranslation();

  const supportingQuestionnaire = folderFile.fileCustomerFolder?.supportingQuestionnaires?.[0];
  const questionnaire = supportingQuestionnaire?.questionnaire;

  const labQuestions: LabQuestion[] = [];

  if (questionnaire?.type?.corpSchema && questionnaire?.content) {
    try {
      const schema = JSON.parse(questionnaire.type.corpSchema) as { properties?: SchemaProperties };
      const parsedContent = JSON.parse(questionnaire.content) as Record<string, boolean>;

      if (schema.properties) {
        labQuestions.push(
          ...Object.entries(schema.properties).map(([code, value]) => ({
            code,
            title: value.title,
            answer: parsedContent[code] as boolean | undefined,
          }))
        );
      }
    } catch (error) {
      throw new Error("Erreur lors du parsing du questionnaire LAB");
    }
  }

  return (
    <Block>
      <Title title={t("pdf.sections.lab.title")} />

      <View style={styles.labQuestionsBlock}>
        {labQuestions.map((question) => {
          const answer = question.answer;
          const riskLevel = answer ? CustomRiskLevel.HIGH : CustomRiskLevel.LOW;

          return (
            <View key={question.code} style={styles.labQuestionRow}>
              {/* Partie gauche (80%) */}
              <View style={styles.labQuestionLeft}>
                <RiskDot riskLevel={riskLevel} />
                <Text style={styles.labQuestionText} wrap>
                  {question.title}
                </Text>
              </View>

              {/* Partie droite (20%) */}
              <View style={styles.labQuestionRight}>
                <View style={styles.labRadioButtonContainer}>
                  <View style={styles.labRadioButton}>
                    {answer === false && <View style={styles.labRadioButtonChecked} />}
                  </View>
                  <Text style={styles.labRadioButtonText}>{t("pdf.sections.lab.no")}</Text>
                </View>
                <View style={styles.labRadioButtonContainer}>
                  <View style={styles.labRadioButton}>
                    {answer === true && <View style={styles.labRadioButtonChecked} />}
                  </View>
                  <Text style={styles.labRadioButtonText}>{t("pdf.sections.lab.yes")}</Text>
                </View>
              </View>
            </View>
          );
        })}
      </View>
    </Block>
  );
};
